import { useEffect, useRef, useState, type ReactNode } from "react";
import { reactedWith, reactors } from "../activity";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { InboxItem } from "../sync/types";
import { RichText } from "../text";
import { Emoji } from "./Emoji";
import { IconAt, IconHelp, IconReaction, IconReply, IconSearch, IconTask } from "./icons";
import { RemindersCard } from "./Reminders";
import { TaskCard } from "./TaskCard";
import { Avatar, Badge, Card, EmptyState, Page } from "./ui";
import { locale, t, tx } from "../i18n";

// Each kind of thing in the inbox has its own chip, with an icon and a
// color, and the same icon on the avatar of who it is from. A mention that
// is only one's keyword is a kind of its own.
type Kind = InboxItem["kind"] | "keyword";
const looks = (): Record<Kind, { label: string; icon: ReactNode }> => ({
  mention: { label: t("Mentioned you"), icon: <IconAt size={14} /> },
  keyword: { label: t("Your keyword"), icon: <IconSearch size={14} /> },
  dm: { label: t("Direct message"), icon: <IconReply size={14} /> },
  reaction: { label: t("Reacted to your message"), icon: <IconReaction size={14} /> },
  task: { label: t("Assigned to you"), icon: <IconTask size={14} /> },
  input: { label: t("Waiting on your answer"), icon: <IconHelp size={14} /> },
});

// InboxView is "needs my attention": mentions, unread DMs, reactions to
// one's messages, and tasks for the user, unread first. Looking reads the
// reactions; they stay marked new until the next visit.
export function InboxView() {
  const session = useSession();
  const state = useLantern();
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const me = state.me?.id;
  // Reactions new when the page opened, kept marked new while it is open.
  const fresh = useRef<Set<string> | null>(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      session.api.inbox().then(
        ({ items }) => {
          if (!live) return;
          session.store.update((s) => items.forEach((it) => it.task && s.tasks.set(it.task.id, it.task)));
          if (!fresh.current) {
            fresh.current = new Set(items.filter((it) => it.kind === "reaction" && it.unread).map((it) => it.message!.id));
            if (fresh.current.size > 0 && document.visibilityState === "visible") void session.api.inboxSeen().catch(() => {});
          }
          setItems(items.map((it) => (it.kind === "reaction" && fresh.current!.has(it.message!.id) ? { ...it, unread: true } : it)));
        },
        () => live && setItems([]),
      );
    void load();
    const t = setInterval(load, 30_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [session, state.taskNotices]);

  const unread = items?.filter((it) => it.unread).length ?? 0;
  // A filter shows one kind of thing at a time; mentions take in DMs, and
  // tasks what waits on an answer.
  const [only, setOnly] = useState<"all" | "mentions" | "reactions" | "tasks">("all");
  const kinds: Record<typeof only, InboxItem["kind"][]> = { all: [], mentions: ["mention", "dm"], reactions: ["reaction"], tasks: ["task", "input"] };
  const shown = only === "all" ? items : (items?.filter((it) => kinds[only].includes(it.kind)) ?? null);
  const kindOf = (it: InboxItem): Kind => (it.kind === "mention" && me && it.message && !it.message.mentions?.includes(me) ? "keyword" : it.kind);
  // Who it is from: who reacted, who wrote, or who asked.
  const from = (it: InboxItem) => it.reactions?.[0]?.principal_id ?? it.message?.author.id ?? (it.task?.requester.kind === "principal" ? it.task.requester.id : undefined);

  return (
    <Page
      className="inbox-view"
      title={t("Inbox")}
      description={t("What needs you: reminders, mentions, unread direct messages, reactions to your messages, and tasks waiting on you, newest first.")}
      actions={unread > 0 ? <Badge tone="accent">{t("{n} unread", { n: unread })}</Badge> : undefined}
    >
      <RemindersCard />
      {items && items.length > 0 && (
        <div className="segmented inbox-filter" role="tablist" aria-label={t("Show")}>
          {(
            [
              ["all", t("All")],
              ["mentions", t("Mentions")],
              ["reactions", t("Reactions")],
              ["tasks", t("Tasks")],
            ] as const
          ).map(([k, name]) => (
            <button key={k} role="tab" aria-selected={only === k} className={only === k ? "active" : ""} onClick={() => setOnly(k)}>
              {name}
            </button>
          ))}
        </div>
      )}
      {items === null && <p className="muted">{t("Loading…")}</p>}
      {items?.length === 0 && (
        <Card>
          <EmptyState icon="🎉" title={t("You're all caught up")}>
            {t("Nothing needs you right now. Mentions, direct messages and tasks for you will show up here.")}
          </EmptyState>
        </Card>
      )}
      {shown && items && items.length > 0 && shown.length === 0 && <p className="muted">{t("Nothing of this kind.")}</p>}
      {shown && shown.length > 0 && (
        <ul className="inbox-list">
          {shown.map((it, i) => {
            const kind = kindOf(it);
            const look = looks()[kind];
            const who = from(it);
            return (
              <li key={i} className={`inbox-item kind-${kind} ${it.unread ? "unread" : ""}`}>
                {who ? (
                  <span className="inbox-avatar">
                    <Avatar id={who} name={session.name(who)} size={36} />
                    <span className="inbox-mark" aria-hidden>
                      {look.icon}
                    </span>
                  </span>
                ) : (
                  <span className="inbox-avatar inbox-icon" aria-hidden>
                    {look.icon}
                  </span>
                )}
                <div className="row-main">
                  <div className="inbox-kind">
                    <span className="inbox-chip">
                      {look.icon}
                      {look.label}
                    </span>
                    {it.kind === "dm" && it.count && it.count > 1 ? <span className="muted"> {t("{n} new", { n: it.count })}</span> : null}
                    {it.unread && <span className="unread-dot" role="img" aria-label={t("unread")} />}
                  </div>
                  {it.message && it.reactions?.length ? (
                    <button className="inbox-message" onClick={() => navigate(`/c/${it.message!.channel_id}/m/${it.message!.id}`)}>
                      <span className="where">
                        {tx("{who} {emoji} in {channel}", {
                          who: <strong>{reactors(it.reactions.map((r) => session.name(r.principal_id)))}</strong>,
                          emoji: (
                            <span className="inbox-reactions">
                              {reactedWith(it.reactions).map((e) => (
                                <Emoji key={e} name={e} />
                              ))}
                            </span>
                          ),
                          channel: session.channelName(it.message.channel_id),
                        })}
                        <span className="muted"> · {new Date(it.at).toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      </span>
                      <span className="text quoted">
                        <RichText text={it.message.text} name={(id) => session.name(id)} me={me} />
                      </span>
                    </button>
                  ) : it.message && (
                    <button className="inbox-message" onClick={() => navigate(`/c/${it.message!.channel_id}/m/${it.message!.id}`)}>
                      <span className="where">
                        {tx("{name} in {channel}", { name: <strong>{session.name(it.message.author.id)}</strong>, channel: session.channelName(it.message.channel_id) })}
                        <span className="muted"> · {new Date(it.message.created_at).toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      </span>
                      <span className="text">
                        <RichText text={it.message.text} name={(id) => session.name(id)} me={me} />
                      </span>
                    </button>
                  )}
                  {it.task && <TaskCard id={it.task.id} />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Page>
  );
}
